import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Tag } from "lucide-react";
import { BLOG_POSTS } from "./BlogPage";

function BlogPostPage() {
  const { slug } = useParams();
  const navigate  = useNavigate();
  const [vw, setVw] = useState(() => window.innerWidth);
  useEffect(() => {
    const h = () => setVw(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const isMobile = vw <= 768;
  const post      = BLOG_POSTS.find((p) => p.slug === slug);
  const others    = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  useEffect(() => {
    if (!post) return;

    const canonical = `https://playonlinesattamatka.com/blog/${post.slug}/`;
    const ogImage   = "https://playonlinesattamatka.com/og-blog.jpg";

    // Title + description
    const prevTitle = document.title;
    const descEl    = document.querySelector("meta[name='description']");
    const prevDesc  = descEl?.getAttribute("content") || "";
    document.title  = post.metaTitle;
    descEl?.setAttribute("content", post.metaDesc);

    // Helper: update existing or create new meta tag; returns cleanup fn
    const restores = [];
    function setMeta(attrName, attrVal, content) {
      let el = document.querySelector(`meta[${attrName}="${attrVal}"]`);
      const existed    = !!el;
      const prevContent = el ? el.getAttribute("content") : null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      restores.push(() => {
        if (!existed) el.remove();
        else el.setAttribute("content", prevContent);
      });
    }

    // OG tags
    setMeta("property", "og:title",       post.metaTitle);
    setMeta("property", "og:description", post.metaDesc);
    setMeta("property", "og:url",         canonical);
    setMeta("property", "og:type",        "article");
    setMeta("property", "og:image",       ogImage);
    setMeta("property", "og:site_name",   "Play Online Satta Matka");

    // Twitter tags
    setMeta("name", "twitter:card",        "summary_large_image");
    setMeta("name", "twitter:title",       post.metaTitle);
    setMeta("name", "twitter:description", post.metaDesc);
    setMeta("name", "twitter:image",       ogImage);
    setMeta("name", "twitter:site",        "@PlaySattaMatka");

    // JSON-LD: BlogPosting + BreadcrumbList
    const schemaEl = document.createElement("script");
    schemaEl.type  = "application/ld+json";
    schemaEl.id    = "__blog-post-schema__";
    schemaEl.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.metaTitle,
        "description": post.metaDesc,
        "image": ogImage,
        "datePublished": "2026-07-19",
        "dateModified":  "2026-07-19",
        "author": { "@type": "Organization", "name": "Play Online Satta Matka", "url": "https://playonlinesattamatka.com/" },
        "publisher": { "@type": "Organization", "name": "Play Online Satta Matka", "url": "https://playonlinesattamatka.com/" },
        "mainEntityOfPage": { "@type": "WebPage", "@id": canonical }
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://playonlinesattamatka.com/" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://playonlinesattamatka.com/blog/" },
          { "@type": "ListItem", "position": 3, "name": post.metaTitle, "item": canonical }
        ]
      }
    ]);
    document.head.appendChild(schemaEl);

    return () => {
      document.title = prevTitle;
      if (descEl) descEl.setAttribute("content", prevDesc);
      restores.forEach(fn => fn());
      document.getElementById("__blog-post-schema__")?.remove();
    };
  }, [post]);

  if (!post) {
    return (
      <main style={pg}>
        <div style={{ textAlign: "center", padding: 80 }}>
          <h1 style={{ fontSize: 24, fontWeight: 900 }}>Post not found</h1>
          <button style={backBtnStyle} onClick={() => navigate("/blog")}>← Back to Blog</button>
        </div>
      </main>
    );
  }

  return (
    <main style={pg}>
      {/* Topbar */}
      <header style={topbar}>
        <button type="button" style={iconBtn} onClick={() => navigate("/blog")}>
          <ArrowLeft size={18} />
        </button>
        <span style={brand}>
          <span style={{ color: "#f97316" }}>PLAY ONLINE</span>
          <span style={{ color: "#16a34a" }}> SATTA MATKA</span>
        </span>
        <button type="button" style={playBtn} onClick={() => navigate("/login")}>Play Now</button>
      </header>

      {/* Breadcrumb */}
      <nav style={breadcrumb} aria-label="Breadcrumb">
        <button style={bcBtn} onClick={() => navigate("/")}>Home</button>
        <span style={bcSep}>›</span>
        <button style={bcBtn} onClick={() => navigate("/blog")}>Blog</button>
        <span style={bcSep}>›</span>
        <span style={bcCurrent}>{post.category}</span>
      </nav>

      {/* Hero */}
      <section style={heroSection}>
        <div style={heroInner}>
          <div style={metaRow}>
            <span style={catBadge}><Tag size={10} /> {post.category}</span>
            <span style={timeBadge}><Clock size={10} /> {post.readTime}</span>
            <span style={dateBadge}>{post.date}</span>
          </div>
          <h1 style={postH1}>{post.title}</h1>
          <p style={postExcerpt}>{post.excerpt}</p>
        </div>
      </section>

      {/* Content */}
      <div style={{ ...contentWrap, gridTemplateColumns: isMobile ? "1fr" : "1fr 320px" }}>
        <article style={article} itemScope itemType="https://schema.org/Article">
          {post.sections.map((sec) => (
            <section key={sec.h2} style={articleSection}>
              <h2 style={h2Style}>{sec.h2}</h2>
              {sec.body.split("\n\n").map((para, i) => (
                para.startsWith("•") ? (
                  <ul key={i} style={ulStyle}>
                    {para.split("\n").filter((l) => l.startsWith("•")).map((line, j) => (
                      <li key={j} style={liStyle}>{line.replace(/^•\s*/, "")}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={i} style={paraStyle}>{para}</p>
                )
              ))}
            </section>
          ))}

          {/* CTA inside article */}
          <div style={articleCta}>
            <h3 style={ctaTitle}>Ready to Play Online Satta Matka?</h3>
            <p style={ctaBody}>Register free in 30 seconds. Best rates guaranteed — Single 9×, Jodi 90×, SP 150×, DP 270×, TP 800×.</p>
            <div style={ctaBtns}>
              <button style={ctaPrimary} onClick={() => navigate("/login")}>Register Free Now</button>
              <button style={ctaSecondary} onClick={() => navigate("/charts")}>View Matka Charts</button>
            </div>
          </div>
        </article>

        {/* Sidebar */}
        <aside style={{ ...sidebar, position: isMobile ? "static" : "sticky", top: isMobile ? "auto" : 70 }}>
          <div style={sideCard}>
            <h3 style={sideTitle}>Play Online Satta Matka</h3>
            <p style={sidePara}>Best game rates in India. Instant deposit & withdrawal. Live results for Kalyan, Milan, Rajdhani, Main Bazar.</p>
            <button style={sidePrimaryBtn} onClick={() => navigate("/login")}>Play Now →</button>
          </div>

          <div style={sideCard}>
            <h3 style={sideTitle}>Game Win Rates</h3>
            {[
              ["Single Digit", "9×"],
              ["Jodi",         "90×"],
              ["Single Patti", "150×"],
              ["Double Patti", "270×"],
              ["Triple Patti", "800×"],
            ].map(([name, rate]) => (
              <div key={name} style={rateRow}>
                <span style={rateName}>{name}</span>
                <span style={rateVal}>{rate}</span>
              </div>
            ))}
          </div>

          <div style={sideCard}>
            <h3 style={sideTitle}>More Articles</h3>
            {others.map((p) => (
              <button key={p.slug} style={sidePost} onClick={() => navigate(`/blog/${p.slug}`)}>
                <span style={sidePostCat}>{p.category}</span>
                <span style={sidePostTitle}>{p.title}</span>
              </button>
            ))}
          </div>
        </aside>
      </div>

      {/* Related posts */}
      <section style={relatedSection}>
        <h2 style={relatedTitle}>More Articles You May Like</h2>
        <div style={{ ...relatedGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)" }}>
          {others.map((p) => (
            <article key={p.slug} style={relCard} onClick={() => navigate(`/blog/${p.slug}`)}>
              <span style={relCat}>{p.category}</span>
              <h3 style={relCardTitle}>{p.title}</h3>
              <p style={relExcerpt}>{p.excerpt}</p>
              <span style={relReadMore}>Read more →</span>
            </article>
          ))}
        </div>
      </section>

      <footer style={ftr}>
        <p style={ftrNote}>© 2026 Play Online Satta Matka | 18+ Only | Play Responsibly</p>
        <div style={ftrLinks}>
          {[["Home", "/"], ["Blog", "/blog"], ["Charts", "/charts"], ["FAQ", "/faq"], ["How to Play", "/how-to-play"], ["Contact Us", "/contact-us"]].map(([l, p]) => (
            <button key={p} style={ftrLnk} onClick={() => navigate(p)}>{l}</button>
          ))}
        </div>
      </footer>
    </main>
  );
}

const pg          = { minHeight: "100vh", background: "#f9fafb", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827" };
const topbar      = { display: "flex", alignItems: "center", gap: 14, padding: "12px 20px", background: "#fff", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, zIndex: 10 };
const iconBtn     = { width: 36, height: 36, border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 };
const brand       = { flex: 1, fontWeight: 900, fontSize: 17, fontFamily: "'Arial Black', sans-serif" };
const playBtn     = { height: 36, padding: "0 16px", border: 0, borderRadius: 8, background: "#f97316", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" };
const backBtnStyle = { marginTop: 20, padding: "10px 20px", border: "2px solid #312e81", background: "#fff", color: "#312e81", fontWeight: 800, fontSize: 14, cursor: "pointer", borderRadius: 8 };

const breadcrumb  = { display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", background: "#fff", borderBottom: "1px solid #f3f4f6", fontSize: 12, flexWrap: "wrap" };
const bcBtn       = { border: 0, background: "transparent", color: "#6b7280", cursor: "pointer", padding: 0, fontWeight: 600 };
const bcSep       = { color: "#9ca3af" };
const bcCurrent   = { color: "#374151", fontWeight: 700 };

const heroSection = { background: "#312e81", color: "#fff", padding: "40px 24px 36px" };
const heroInner   = { maxWidth: 760, margin: "0 auto" };
const metaRow     = { display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" };
const catBadge    = { display: "flex", alignItems: "center", gap: 4, background: "#f97316", color: "#fff", fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 999 };
const timeBadge   = { display: "flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,.15)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 999 };
const dateBadge   = { color: "rgba(255,255,255,.7)", fontSize: 12 };
const postH1      = { margin: "0 0 14px", fontSize: "clamp(22px,4vw,34px)", fontWeight: 900, lineHeight: 1.25 };
const postExcerpt = { margin: 0, fontSize: 16, color: "rgba(255,255,255,.8)", lineHeight: 1.6 };

const contentWrap = { display: "grid", gridTemplateColumns: "1fr 320px", gap: 28, maxWidth: 1100, margin: "0 auto", padding: "36px 24px 48px", alignItems: "start" };
const article     = { minWidth: 0 };
const articleSection = { marginBottom: 28 };
const h2Style     = { margin: "0 0 12px", fontSize: "clamp(18px,2.5vw,22px)", fontWeight: 900, color: "#111827", borderLeft: "4px solid #f97316", paddingLeft: 14 };
const paraStyle   = { margin: "0 0 12px", fontSize: 15, color: "#374151", lineHeight: 1.75 };
const ulStyle     = { margin: "0 0 12px", paddingLeft: 20 };
const liStyle     = { fontSize: 15, color: "#374151", lineHeight: 1.75, marginBottom: 6 };

const articleCta  = { background: "linear-gradient(135deg,#312e81,#1d4ed8)", borderRadius: 16, padding: "28px 24px", color: "#fff", marginTop: 32 };
const ctaTitle    = { margin: "0 0 8px", fontSize: 20, fontWeight: 900 };
const ctaBody     = { margin: "0 0 18px", fontSize: 14, color: "rgba(255,255,255,.85)", lineHeight: 1.6 };
const ctaBtns     = { display: "flex", gap: 10, flexWrap: "wrap" };
const ctaPrimary  = { height: 44, padding: "0 22px", border: 0, borderRadius: 10, background: "#f97316", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" };
const ctaSecondary = { height: 44, padding: "0 20px", border: "2px solid rgba(255,255,255,.5)", borderRadius: 10, background: "transparent", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" };

const sidebar     = { display: "flex", flexDirection: "column", gap: 16, position: "sticky", top: 70 };
const sideCard    = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 16px" };
const sideTitle   = { margin: "0 0 10px", fontSize: 15, fontWeight: 800, color: "#111827" };
const sidePara    = { margin: "0 0 14px", fontSize: 13, color: "#6b7280", lineHeight: 1.6 };
const sidePrimaryBtn = { width: "100%", height: 40, border: 0, borderRadius: 10, background: "#f97316", color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" };
const rateRow     = { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13 };
const rateName    = { color: "#374151" };
const rateVal     = { fontWeight: 800, color: "#f97316" };
const sidePost    = { width: "100%", textAlign: "left", border: 0, background: "transparent", padding: "10px 0", borderBottom: "1px solid #f3f4f6", cursor: "pointer", display: "flex", flexDirection: "column", gap: 4 };
const sidePostCat = { fontSize: 10, fontWeight: 800, color: "#2563eb", textTransform: "uppercase" };
const sidePostTitle = { fontSize: 13, fontWeight: 700, color: "#111827", lineHeight: 1.4 };

const relatedSection = { background: "#fff", padding: "40px 24px", borderTop: "1px solid #e5e7eb" };
const relatedTitle = { margin: "0 0 20px", fontSize: 20, fontWeight: 900, color: "#111827", maxWidth: 1100, marginLeft: "auto", marginRight: "auto" };
const relatedGrid  = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, maxWidth: 1100, margin: "0 auto" };
const relCard     = { border: "1px solid #e5e7eb", borderRadius: 14, padding: "18px 16px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 8 };
const relCat      = { fontSize: 11, fontWeight: 800, color: "#2563eb", textTransform: "uppercase" };
const relCardTitle = { margin: 0, fontSize: 15, fontWeight: 800, color: "#111827", lineHeight: 1.4 };
const relExcerpt  = { margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6, flex: 1 };
const relReadMore  = { fontSize: 13, fontWeight: 700, color: "#f97316" };

const ftr         = { background: "#111827", color: "#fff", padding: "24px", textAlign: "center" };
const ftrNote     = { fontSize: 12, color: "#9ca3af", marginBottom: 12 };
const ftrLinks    = { display: "flex", flexWrap: "wrap", gap: "6px 16px", justifyContent: "center" };
const ftrLnk      = { border: 0, background: "transparent", color: "#d1d5db", fontSize: 13, fontWeight: 600, cursor: "pointer" };

export default BlogPostPage;
